import type { Subject, TextbookPublisher, GradeSemester } from '../../shared/types';

export interface KPEntry {
  id: string;
  label: string;
  status: 'ready' | 'wip';
  /** 官方 108 課綱學習內容代碼，例如 N-3-3a。格式：<領域>-<階段>-<流水號><子項>。 */
  moeCode?: string;
  /**
   * 學年度小數標記：3.1 = 三上（Grade 3 Semester 1），3.2 = 三下（預設）、
   * 4.1 = 四上、4.2 = 四下。未填時預設為 3.2（既有三下 KP 保持不變）。
   */
  grade?: GradeSemester;
  /**
   * 題目來源類型：
   * - 'parametric'  → 由程式生成器（MATH_GENERATORS）產生，無 JSON bank
   * - 'handwritten' → 手寫 JSON bank（預設，未填即視為 handwritten）
   * - 'mixed'       → 同時有 generator 與 JSON bank
   */
  type?: 'parametric' | 'handwritten' | 'mixed';
}

export interface ChapterEntry {
  number: number;
  title: string;
  kps: KPEntry[];
  /**
   * 對應的 108 課綱學習內容代碼（一個章節可能跨多個代碼）。
   * 格式：逗號分隔的代碼串，例如 "N-3-3,N-3-5"。
   * 若課綱無對應獨立條目請寫 "no-official-match"。
   */
  moeRef?: string;
}

export interface SubjectCurriculum {
  subject: Subject;
  label: string;
  emoji: string;
  textbook: TextbookPublisher;
  chapters: ChapterEntry[];
}

/**
 * 108 課綱對照說明
 * ─────────────────────────────────────────────────────────────────
 * 三年級屬於「第二學習階段」(Stage II，3-4 年級)。
 *
 * 數學：代碼系統 N-3-x（數與量）、S-3-x（空間與形狀）、D-3-x（資料與不確定性）
 *   - 來源：國家教育研究院 / 均一教育平台 (junyiacademy.org)
 *   - URL：https://www.junyiacademy.org/v108_g03
 *   - 官方 PDF：https://www.naer.edu.tw/upload/1/16/doc/815/…數學領域.pdf
 *
 * 國語文：代碼系統 Aa-II-x（文字篇章）、Ba-II-x（文本表述）、Ca-II-x（文化內涵）
 *   - 來源：yowlab.idv.tw 整理 / 官方 k12ea.gov.tw PDF
 *   - URL：https://yowlab.idv.tw/wordpress/?p=262
 *
 * 英語：代碼系統 Aa-II-x（字母）、Ab-II-x（語音）、Ac-II-x（字詞）、Ad-II-x（句構）
 *   - 來源：官方 k12ea.gov.tw PDF（英語文領域）
 *   - 注意：108 課綱英語從三年級才開始，無單獨「三下」條目，
 *     整個三四年級共用第二學習階段同一組條目。
 *
 * 自然科學：代碼系統 INa-II-x … INg-II-x
 *   - 來源：guidelines.ms.cshs.tc.edu.tw/108/nature
 *   - 注意：自然科學課綱以兩年為一階段，同一代碼跨三四年級；
 *     南一版三下的實際選題由教科書決定。
 *
 * 社會：整合歷史/地理/公民；代碼前碼待查（官方 PDF 為二進位，無法直接解析）。
 *   - 來源：官方 k12ea.gov.tw PDF（社會領域）
 *   - 注意：社會領域 PDF 無法解析，社會相關代碼標為 pending，
 *     以翰林版三年級單元名稱與四大主題軸對齊。
 * ─────────────────────────────────────────────────────────────────
 */
export const CURRICULUM: SubjectCurriculum[] = [
  // ────────────────────────────────────────────────────────────────
  // ⚡ 數學 (康軒) — 對齊 108 課綱 第二學習階段
  // 參考來源：均一教育平台 https://www.junyiacademy.org/v108_g03
  //           國家教育研究院 https://www.naer.edu.tw/upload/1/16/doc/815/…數學領域.pdf
  // ────────────────────────────────────────────────────────────────
  {
    subject: 'math',
    label: '數學',
    emoji: '⚡',
    textbook: '康軒',
    chapters: [
      // ════════════════════════════════════════════════════════════════
      // 三下(grade 3.2)真課本 9 單元 — 康軒第六冊,boss 2026-06-14 親拍課本目錄修正。
      // 權威來源:docs/CURRICULUM-數學-康軒-三下-units.md(取代先前 WebSearch 猜測版)。
      // ⚠️ KP id 一律保留(產生器/題庫靠 id 路由),只重新歸到對的真單元 + 重排順序。
      // 三上內容(萬以內的數/加減直式/公釐/面積/角/正方形長方形)已下移到本檔三上 review 區。
      // ════════════════════════════════════════════════════════════════
      {
        number: 1,
        title: '分數的加減',
        moeRef: 'N-3-9',
        // ①和 1 一樣大的分數 ②分數的加法 ③分數的減法
        kps: [
          {
            id: 'math.fraction.basic',
            label: '和 1 一樣大的分數 / 同分母分數的意義',
            status: 'ready',
            grade: 3.2,
            type: 'handwritten',
            moeCode: 'N-3-9',
            // N-3-9：簡單同分母分數，比較、加減，結果不超過 2
          },
          {
            id: 'math.fraction.compare',
            label: '同分母分數的加法與減法',
            status: 'ready',
            grade: 3.2,
            moeCode: 'N-3-9',
            type: 'parametric',
          },
        ],
      },
      {
        number: 2,
        title: '除法',
        moeRef: 'N-3-4,N-3-5,N-3-7',
        // ①二位數除以一位數 ②三位數除以一位數 ④減與除的兩步驟問題
        kps: [
          {
            id: 'math.div.no-remainder',
            label: '二 / 三位數除以一位數（直式整除）',
            status: 'ready',
            grade: 3.2,
            moeCode: 'N-3-5',
            type: 'parametric',
            // N-3-5：除以一位數：除法直式計算（被除數二、三位）
          },
          {
            id: 'math.div.remainder',
            label: '有餘數的除法',
            status: 'ready',
            grade: 3.2,
            moeCode: 'N-3-4',
            type: 'parametric',
            // N-3-4：除法的意義與應用，連結乘法
          },
          {
            id: 'math.word.two-step',
            label: '減與除的兩步驟問題',
            status: 'ready',
            grade: 3.2,
            moeCode: 'N-3-7',
            type: 'parametric',
            // N-3-7：解題：兩步驟應用問題（含減與除）
          },
        ],
      },
      {
        number: 3,
        title: '尋找規律',
        moeRef: 'pending',
        // ①圖形的規律 ②數字的規律 ③數形規律的應用 — 暫無產生器(status:'wip')。
        kps: [
          {
            id: 'math.pattern.shape',
            label: '圖形的規律',
            status: 'wip',
            grade: 3.2,
            moeCode: 'pending',
            // 真課本單元 3「尋找規律」①圖形規律;暫無產生器,待補。
          },
          {
            id: 'math.pattern.number',
            label: '數字的規律與數形規律應用',
            status: 'wip',
            grade: 3.2,
            moeCode: 'pending',
            // 真課本單元 3「尋找規律」②數字規律 ③數形規律應用;暫無產生器,待補。
          },
        ],
      },
      {
        number: 4,
        title: '公斤和公克',
        moeRef: 'N-3-16',
        // ①認識 1 公斤秤 ②認識 3 公斤秤 ③重量量感估測 ④公斤公克換算 ⑤重量加減
        kps: [
          {
            id: 'math.weight.kg',
            label: '公斤和公克的認識、換算與加減',
            status: 'ready',
            grade: 3.2,
            moeCode: 'N-3-16',
            // N-3-16：重量「公斤」「公克」，實測與單位換算
          },
        ],
      },
      {
        number: 5,
        title: '小數',
        moeRef: 'N-3-10',
        // ①認識一位小數 ②小數的大小比較 ④小數的加減 ⑤1 毫米＝0.1 公分
        kps: [
          {
            id: 'math.decimal.basic',
            label: '認識一位小數與大小比較',
            status: 'ready',
            grade: 3.2,
            type: 'parametric',
            moeCode: 'N-3-10',
            // N-3-10：一位小數，結合位值表，比較、加減含直式
          },
          {
            id: 'math.decimal.addsub',
            label: '一位小數的加減計算',
            status: 'ready',
            grade: 3.2,
            moeCode: 'N-3-10',
            type: 'parametric',
          },
        ],
      },
      {
        number: 6,
        title: '圓',
        moeRef: 'S-3-3',
        // ①認識圓 ②使用圓規
        kps: [
          {
            id: 'math.shape.circle',
            label: '認識圓、半徑、直徑與使用圓規',
            status: 'ready',
            grade: 3.2,
            type: 'handwritten',
            moeCode: 'S-3-3',
            // S-3-3：圓心、圓周、半徑、直徑，用圓規畫圓
          },
        ],
      },
      {
        number: 7,
        title: '乘法與除法',
        moeRef: 'N-3-3,N-3-6',
        // ①乘與除的關係 ②被乘數或乘數未知 ③被除數或除數未知(乘除關係 + 算式填空)
        kps: [
          {
            id: 'math.div.mul-relation',
            label: '乘與除的關係、未知數算式填空',
            status: 'ready',
            grade: 3.2,
            moeCode: 'N-3-6',
            type: 'parametric',
            // N-3-6：解題：乘除應用問題，連結乘與除的關係
          },
          {
            id: 'math.mul.2x1',
            label: '兩位數 × 一位數',
            status: 'ready',
            grade: 3.2,
            moeCode: 'N-3-3',
            type: 'parametric',
            // N-3-3：乘以一位數（被乘數二、三位）
          },
          {
            id: 'math.mul.2x2',
            label: '兩位數 × 兩位數',
            status: 'ready',
            grade: 3.2,
            moeCode: 'N-3-3',
            type: 'parametric',
          },
          {
            id: 'math.mul.3x1',
            label: '三位數 × 一位數',
            status: 'ready',
            grade: 3.2,
            moeCode: 'N-3-3',
            type: 'parametric',
          },
        ],
      },
      {
        number: 8,
        title: '時間',
        moeRef: 'N-3-17',
        // ①1 日 24 小時 ②時、分、秒 ③時分秒的加減計算
        kps: [
          {
            id: 'math.time.clock',
            label: '1 日 24 小時 / 時、分、秒',
            status: 'ready',
            grade: 3.2,
            type: 'parametric',
            moeCode: 'N-3-17',
            // N-3-17：時間「日」「時」「分」「秒」，單位換算
          },
          {
            id: 'math.time.duration',
            label: '時分秒的加減計算',
            status: 'ready',
            grade: 3.2,
            type: 'parametric',
            moeCode: 'N-3-17',
          },
        ],
      },
      {
        number: 9,
        title: '統計表',
        moeRef: 'D-3-1',
        // ①報讀一維表格 ②報讀二維表格 ③製作統計表及應用
        kps: [
          {
            id: 'math.stats.table',
            label: '報讀一維與二維表格',
            status: 'ready',
            grade: 3.2,
            moeCode: 'D-3-1',
            // D-3-1：一維表格與二維表格，報讀、說明與製作
          },
          {
            id: 'math.stats.bar',
            label: '製作統計表與長條圖',
            status: 'ready',
            grade: 3.2,
            type: 'handwritten',
            moeCode: 'D-3-1',
          },
          {
            id: 'math.stats.pictograph',
            label: '圖示統計表（每圖示代表 N 人）',
            status: 'ready',
            grade: 3.2,
            type: 'handwritten',
            moeCode: 'D-3-1',
          },
        ],
      },
      // ════════════════════════════════════════════════════════════════
      // 三上 review 區(grade 3.1)— 原本誤混進三下主清單的三上單元下移於此。
      // 真課本三下不含:萬以內的數 / 加減直式 / 公釐(長度) / 面積 / 角 / 正方形長方形。
      // (docs/CURRICULUM-數學-康軒-三下-units.md 課本目錄頁標註這些屬「3 上」。)
      // KP id 全保留,只改 grade:3.1 並移出三下;產生器/題庫路由不受影響(按 id)。
      // ════════════════════════════════════════════════════════════════
      {
        number: 25,
        title: '【三上】萬以內的數與加減直式',
        moeRef: 'N-3-1,N-3-2',
        kps: [
          {
            id: 'math.number.thousand',
            label: '千位數的認識與比較',
            status: 'ready',
            grade: 3.1,
            type: 'parametric',
            moeCode: 'N-3-1',
            // N-3-1：一萬以內的數，位值單位「千」（三上內容）
          },
          {
            id: 'math.calc.addsub-vertical',
            label: '加減直式計算（含多次進退位）',
            status: 'ready',
            grade: 3.1,
            moeCode: 'N-3-2',
            type: 'parametric',
            // N-3-2：加減直式計算，含加減法多次進退位（三上內容）
          },
        ],
      },
      {
        number: 26,
        title: '【三上】長度（公釐）、面積與角',
        moeRef: 'N-3-12,N-3-14,S-3-1',
        kps: [
          {
            id: 'math.length.cm',
            label: '毫米（mm）的認識與換算',
            status: 'ready',
            grade: 3.1,
            moeCode: 'N-3-12',
            // N-3-12：長度「毫米」，實測、量感、估測與計算（三上內容）
          },
          {
            id: 'math.area.rectangle',
            label: '長方形面積（平方公分）',
            status: 'ready',
            grade: 3.1,
            type: 'parametric',
            moeCode: 'N-3-14',
            // N-3-14：面積「平方公分」，實測、量感、估測與計算（三上內容）
          },
          {
            id: 'math.angle.basic',
            label: '角與直角的認識',
            status: 'ready',
            grade: 3.1,
            moeCode: 'S-3-1',
            // S-3-1（同 N-3-13）：角與角度，含直角（三上內容）
          },
        ],
      },
      {
        number: 27,
        title: '【三上】正方形與長方形',
        moeRef: 'S-3-2',
        kps: [
          {
            id: 'math.shape.rectangle',
            label: '正方形與長方形的邊角特徵',
            status: 'ready',
            grade: 3.1,
            type: 'handwritten',
            moeCode: 'S-3-2',
            // S-3-2：正方形和長方形以邊與角特徵定義（三上內容）
          },
        ],
      },
      // ── 三上 backport 章節 (grade: 3.1) — Y1.Q3 v1.1 第一批 ───────
      // 均一 k-m3a-c01..c09；標 grade:3.1 與既有三下 grade:3.2 KP 區分
      {
        number: 10,
        title: '【三上】數到 10000（萬以內的數）',
        moeRef: 'N-3-1',
        kps: [
          {
            id: 'math.number.10000',
            label: '整數量感 1–10000',
            status: 'ready',
            moeCode: 'N-3-1',
            grade: 3.1,
            type: 'parametric',
          },
          {
            id: 'math.place.value.4digits',
            label: '四位數位值（個十百千）',
            status: 'ready',
            moeCode: 'N-3-1',
            grade: 3.1,
            type: 'parametric',
          },
        ],
      },
      {
        number: 11,
        title: '【三上】加減直式（多次進退位）',
        moeRef: 'N-3-2',
        kps: [
          {
            id: 'math.add.carry.multi',
            label: '多次進位加法直式',
            status: 'ready',
            moeCode: 'N-3-2',
            grade: 3.1,
            type: 'parametric',
          },
          {
            id: 'math.sub.borrow.multi',
            label: '多次退位減法直式',
            status: 'ready',
            moeCode: 'N-3-2',
            grade: 3.1,
            type: 'parametric',
          },
        ],
      },
      {
        number: 12,
        title: '【三上】乘法直式（兩位數 × 一位數，含進位）',
        moeRef: 'N-3-3',
        kps: [
          {
            id: 'math.mul.2x1.review',
            label: '兩位數 × 一位數（三上初學版）',
            status: 'ready',
            moeCode: 'N-3-3',
            grade: 3.1,
            type: 'parametric',
          },
        ],
      },
      {
        number: 13,
        title: '【三上】除法初步（整除，無餘數）',
        moeRef: 'N-3-5',
        kps: [
          {
            id: 'math.div.1digit.no-rem',
            label: '除以一位數（無餘數）',
            status: 'ready',
            moeCode: 'N-3-5',
            grade: 3.1,
            type: 'parametric',
          },
        ],
      },
      {
        number: 14,
        title: '【三上】時間計算（時、分、秒）',
        moeRef: 'N-3-8',
        kps: [
          {
            id: 'math.time.elapsed',
            label: '鐘面時刻讀取與時間加減',
            status: 'ready',
            moeCode: 'N-3-8',
            grade: 3.1,
            type: 'parametric',
          },
        ],
      },
      {
        number: 15,
        title: '【三上】分數初步（1/2、1/3、1/4 認識）',
        moeRef: 'N-3-9',
        kps: [
          {
            id: 'math.frac.unit',
            label: '單位分數認識與比較',
            status: 'ready',
            moeCode: 'N-3-9',
            grade: 3.1,
          },
        ],
      },
      {
        number: 30,
        title: '【三上】幾何圖形初步（分數情境＋平分概念）',
        moeRef: 'N-3-9,S-3-3',
        kps: [
          {
            id: 'math.shape.circle.up',
            label: '圓形與長方形的平分（分數情境）',
            status: 'ready',
            moeCode: 'N-3-9',
            grade: 3.1,
            type: 'handwritten',
          },
        ],
      },
      // ── 四上 backport 章節 (grade: 4.1) — Y1.Q4 v1.2 第一批 ───────────────
      {
        number: 16,
        title: '【四上】大數的認識（萬、十萬以上）',
        moeRef: 'N-4-1',
        kps: [
          {
            id: 'math.large.number.4up',
            label: '萬以上整數量感、位值與大小比較',
            status: 'ready',
            moeCode: 'N-4-1',
            grade: 4.1,
            type: 'parametric',
          },
        ],
      },
      {
        number: 17,
        title: '【四上】進位加減法（四、五位數直式）',
        moeRef: 'N-4-2',
        kps: [
          {
            id: 'math.add.sub.multi.4up',
            label: '四～五位數多次進退位加減直式',
            status: 'ready',
            moeCode: 'N-4-2',
            grade: 4.1,
            type: 'parametric',
          },
        ],
      },
      {
        number: 18,
        title: '【四上】乘法直式（兩位×兩位、三位×一位進階）',
        moeRef: 'N-4-3',
        kps: [
          {
            id: 'math.mul.2x2.4up',
            label: '兩位數 × 兩位數（四上進階，大數多次進位）',
            status: 'ready',
            moeCode: 'N-4-3',
            grade: 4.1,
            type: 'parametric',
          },
          {
            id: 'math.mul.3x1.4up',
            label: '三位數 × 一位數（四上進階版）',
            status: 'ready',
            moeCode: 'N-4-3',
            grade: 4.1,
            type: 'parametric',
          },
        ],
      },
      {
        number: 19,
        title: '【四上】兩步驟乘除應用題',
        moeRef: 'N-4-4',
        kps: [
          {
            id: 'math.word.two-step.4up',
            label: '四上兩步驟乘除應用、多步驟解題',
            status: 'ready',
            moeCode: 'N-4-4',
            grade: 4.1,
            type: 'parametric',
          },
        ],
      },
      {
        number: 20,
        title: '【四上】同分母分數加減與角度量角器',
        moeRef: 'N-4-5,S-4-1',
        kps: [
          {
            id: 'math.frac.add.same-denom.4up',
            label: '同分母分數加減、帶分數轉換',
            status: 'ready',
            moeCode: 'N-4-5',
            grade: 4.1,
            type: 'parametric',
          },
          {
            id: 'math.angle.protractor.4up',
            label: '角度量角器：量角、畫角（角度初步）',
            status: 'ready',
            moeCode: 'S-4-1',
            grade: 4.1,
            type: 'handwritten',
          },
        ],
      },
      {
        number: 21,
        title: '【四上】兩位小數（百分位）的加減計算',
        moeRef: 'N-4-6',
        kps: [
          {
            id: 'math.decimal.2place.4up',
            label: '兩位小數的加減直式與百分位位值',
            status: 'ready',
            moeCode: 'N-4-6',
            grade: 4.1,
            type: 'parametric',
          },
        ],
      },
      {
        number: 22,
        title: '【四上】時間的複雜計算（時↔分↔秒、日期）',
        moeRef: 'N-4-7',
        kps: [
          {
            id: 'math.time.complex.4up',
            label: '時間換算、跨整點時間差、週日換算',
            status: 'ready',
            moeCode: 'N-4-7',
            grade: 4.1,
            type: 'parametric',
          },
        ],
      },
      {
        number: 23,
        title: '【四上】體積初步（立方公分）',
        moeRef: 'N-4-8',
        kps: [
          {
            id: 'math.volume.basic.4up',
            label: '體積的意義、立方公分與長方體體積',
            status: 'ready',
            moeCode: 'N-4-8',
            grade: 4.1,
            type: 'handwritten',
          },
        ],
      },
      {
        number: 24,
        title: '【四上】統計長條圖（讀圖與製作）',
        moeRef: 'D-4-1',
        kps: [
          {
            id: 'math.stats.bar.4up',
            label: '四上長條圖判讀、比較與製作',
            status: 'ready',
            moeCode: 'D-4-1',
            grade: 4.1,
            type: 'handwritten',
          },
        ],
      },

      // ── stage6 wire: meow911 段考真題綜合題庫 ─────────────────────
      // 數學 37 題段考真題:6 個 chapter-aligned bank < 15Q,#250 coverage-fix
      // 已合併進 math.meow911.misc(共 37 題);保留單一綜合題庫 KP。
      {
        number: 50,
        title: '【三下】📚 數學段考真題庫(綜合)',
        moeRef: 'pending',
        // stage6 wire / meow911:37 題段考真題收綜合題庫(#250 整治)
        kps: [
          { id: 'math.meow911.misc', label: '📚 數學綜合段考真題(37 題,含 ch2-ch7 + 跨單元)',
            status: 'ready', grade: 3.2, type: 'handwritten', moeCode: 'pending' },
        ],
      },
    ],
  },

  // ────────────────────────────────────────────────────────────────
  // 🔮 國語 (康軒) — 對齊 108 課綱 第二學習階段
  // 參考來源：yowlab.idv.tw 整理 https://yowlab.idv.tw/wordpress/?p=262
  //           官方 k12ea.gov.tw PDF（語文領域-國語文）
  // ────────────────────────────────────────────────────────────────
  {
    subject: 'chinese',
    label: '國語',
    emoji: '🔮',
    textbook: '康軒',
    chapters: [
      // ── 三下 by-lesson 章節(spec #76 vertical slice) ─────────────────
      // 康軒三下 12 課,每課獨立 chapter + 50 題 bank。L01 為 vertical slice
      // PoC;通過 boss 試玩後 L02-L12 批量產出。
      // 2026-05-22: boss flag「找不到第一課」— 從 array 末位移到首位,
      // chapter-detail by array order render,L01 現在會是國文 chapter list 第 1 個。
      {
        number: 13,
        title: '📖 第一課:許願(三下康軒)',
        moeRef: 'Aa-II-1,Ab-II-3,Ad-II-2',
        kps: [
          {
            id: 'chinese.l01.xuyuan',
            label: '第一課 許願(生字 / 課文理解 / 句型 / 寫作)',
            status: 'ready',
            grade: 3.2,
            type: 'handwritten',
            moeCode: 'Aa-II-1',
            // L01 主題:願望期盼;代表性生字:虔誠、耀、許願、晨光、樹苗
          },
        ],
      },
      {
        number: 14,
        title: '📖 第二課:下雨的時候(三下康軒)',
        moeRef: 'Aa-II-1,Ab-II-3,Ad-II-2',
        kps: [
          {
            id: 'chinese.l02.xiayushihou',
            label: '第二課 下雨的時候(生字 / 課文 / 句型 / 寫作)',
            status: 'ready', grade: 3.2, type: 'handwritten', moeCode: 'Ab-II-3',
            // L02 主題:自然天氣、生活情境觀察;代表性生字:屋簷、順利、陷、垂掛、感覺
          },
        ],
      },
      {
        number: 15,
        title: '📖 第三課:遇見美如奶奶(三下康軒)',
        moeRef: 'Ab-II-3,Ba-II-1,Cb-II-1',
        kps: [
          {
            id: 'chinese.l03.yujianmeiruunainai',
            label: '第三課 遇見美如奶奶(生字 / 課文 / 句型 / 寫作)',
            status: 'ready', grade: 3.2, type: 'handwritten', moeCode: 'Ba-II-1',
            // L03 主題:人物互動、世代溫情;代表性生字:分區、魔法、暗號、腦子、電腦
          },
        ],
      },
      {
        number: 16,
        title: '📖 第四課:工匠之祖(三下康軒)',
        moeRef: 'Ab-II-3,Ba-II-1,Ca-II-1',
        kps: [
          {
            id: 'chinese.l04.gongjiangzhizu',
            label: '第四課 工匠之祖(生字 / 課文 / 句型 / 寫作)',
            status: 'ready', grade: 3.2, type: 'handwritten', moeCode: 'Ca-II-1',
            // L04 主題:傳統工藝文化、歷史認知;代表性生字:察、工匠、鼻祖、魯班、王宮
          },
        ],
      },
      {
        number: 17,
        title: '📖 第五課:學田鼠開路(三下康軒)',
        moeRef: 'Ab-II-3,Ba-II-1,Bc-II-1',
        kps: [
          {
            id: 'chinese.l05.xuetianshukai',
            label: '第五課 學田鼠開路(生字 / 課文 / 句型 / 寫作)',
            status: 'ready', grade: 3.2, type: 'handwritten', moeCode: 'Bc-II-1',
            // L05 主題:動物寓言、解決問題;代表性生字:爾、建議、建、方便、設計
          },
        ],
      },
      {
        number: 18,
        title: '📖 第六課:神奇密碼(三下康軒)',
        moeRef: 'Ab-II-3,Ba-II-1,Bc-II-1',
        kps: [
          {
            id: 'chinese.l06.shenqimima',
            label: '第六課 神奇密碼(生字 / 課文 / 句型 / 寫作)',
            status: 'ready', grade: 3.2, type: 'handwritten', moeCode: 'Bc-II-1',
            // L06 主題:邏輯推理、創意想像;代表性生字:科技、錢包、人類、碼、目
          },
        ],
      },
      {
        number: 19,
        title: '📖 第七課:油桐花.五月雪(三下康軒)',
        moeRef: 'Ab-II-3,Ba-II-1,Ac-II-2',
        kps: [
          {
            id: 'chinese.l07.youtonghua',
            label: '第七課 油桐花.五月雪(生字 / 課文 / 句型 / 寫作)',
            status: 'ready', grade: 3.2, type: 'handwritten', moeCode: 'Ac-II-2',
            // L07 主題:自然景物、季節主題;代表性生字:鋪、瓣、掌、禮、梢
          },
        ],
      },
      {
        number: 20,
        title: '📖 第八課:大自然的美術館(三下康軒)',
        moeRef: 'Ab-II-3,Ba-II-1,Ac-II-2',
        kps: [
          {
            id: 'chinese.l08.daziranmeishuguan',
            label: '第八課 大自然的美術館(生字 / 課文 / 句型 / 寫作)',
            status: 'ready', grade: 3.2, type: 'handwritten', moeCode: 'Ac-II-2',
            // L08 主題:自然美感、藝術融合(野柳奇景);代表性生字:腐、野、野柳、地質、海岬
          },
        ],
      },
      {
        number: 21,
        title: '📖 第九課:臺灣的山椒魚(三下康軒)',
        moeRef: 'Ab-II-3,Bc-II-1,Ca-II-1',
        kps: [
          {
            id: 'chinese.l09.taiwandeshanjiaoyu',
            label: '第九課 臺灣的山椒魚(生字 / 課文 / 句型 / 寫作)',
            status: 'ready', grade: 3.2, type: 'handwritten', moeCode: 'Bc-II-1',
            // L09 主題:本土生物、科普(保育、瀕危物種);代表性生字:伐、躍、縫、敵、潤
          },
        ],
      },
      {
        number: 22,
        title: '📖 第十課:漁夫和金魚(三下康軒)',
        moeRef: 'Ab-II-3,Ba-II-1,Cb-II-1',
        kps: [
          {
            id: 'chinese.l10.yufuhejinyu',
            label: '第十課 漁夫和金魚(生字 / 課文 / 句型 / 寫作)',
            status: 'ready', grade: 3.2, type: 'handwritten', moeCode: 'Ba-II-1',
            // L10 主題:民間故事改編、文化傳承(知足常樂);代表性生字:惱、藻、破、隆、聊
          },
        ],
      },
      {
        number: 23,
        title: '📖 第十一課:聰明的鼠鹿(三下康軒)',
        moeRef: 'Ab-II-3,Ba-II-1,Cb-II-1',
        kps: [
          {
            id: 'chinese.l11.congmingdeshulu',
            label: '第十一課 聰明的鼠鹿(生字 / 課文 / 句型 / 寫作)',
            status: 'ready', grade: 3.2, type: 'handwritten', moeCode: 'Ba-II-1',
            // L11 主題:動物智慧、品格教育(智慧勝過蠻力);代表性生字:砰、布幕、老虎、飢餓、不夠
          },
        ],
      },
      {
        number: 24,
        title: '📖 第十二課:還要跌幾次(三下康軒)',
        moeRef: 'Ab-II-3,Ba-II-1,Cb-II-1',
        kps: [
          {
            id: 'chinese.l12.haiyaodiejici',
            label: '第十二課 還要跌幾次(生字 / 課文 / 句型 / 寫作)',
            status: 'ready', grade: 3.2, type: 'handwritten', moeCode: 'Ba-II-1',
            // L12 主題:堅毅精神、勵志(百折不撓);代表性生字:老伯、跌倒、路段、陡坡、村民
          },
        ],
      },
      {
        number: 1,
        title: '注音符號的應用',
        moeRef: 'Aa-II-1',
        kps: [
          {
            id: 'chinese.zhuyin.basic',
            label: '注音拼讀與標注',
            status: 'ready',
            moeCode: 'Aa-II-1',
            // Aa-II-1：標注注音符號的各類文本
          },
          {
            id: 'chinese.phonetic.tone',
            label: '字音辨識（多音字、聲調、輕聲）',
            status: 'ready',
            moeCode: 'Aa-II-1',
            // Aa-II-1：標注注音符號；多音字聲調辨別
          },
        ],
      },
      {
        number: 2,
        title: '識字、部首與詞語',
        moeRef: 'Ab-II-1,Ab-II-2,Ab-II-3',
        kps: [
          {
            id: 'chinese.vocab.usage',
            label: '語詞應用（搭配、近反義、修辭）',
            status: 'ready',
            moeCode: 'Ab-II-3',
            // Ab-II-3：常用詞彙，詞語的選用與應用
          },
          {
            id: 'chinese.character.radical',
            label: '部首與字形識別',
            status: 'ready',
            grade: 3.2,
            type: 'handwritten',
            moeCode: 'Ab-II-2',
            // Ab-II-2：常用字的字形（含部件、部首）
          },
        ],
      },
      {
        number: 3,
        title: '句段與段落大意',
        moeRef: 'Ac-II-1,Ad-II-1,Ad-II-2',
        kps: [
          {
            id: 'chinese.segment.summary',
            label: '段落主旨歸納',
            status: 'ready',
            moeCode: 'Ad-II-2',
            // Ad-II-2：主要段落的大意
          },
          {
            id: 'chinese.sentence.pattern',
            label: '常見句型（因果、轉折、標點、修辭）',
            status: 'ready',
            moeCode: 'Ac-II-1',
            // Ac-II-1：標點符號的使用；句型
          },
        ],
      },
      {
        number: 4,
        title: '閱讀理解',
        moeRef: 'Ad-II-1,Ad-II-3,Bc-II-1,Bc-II-2',
        kps: [
          {
            id: 'chinese.comprehension.basic',
            label: '記敘文短文理解（200 字短文 × 20Q）',
            status: 'ready',
            moeCode: 'Ba-II-1',
            // Ba-II-1：記敘文本的結構（時間、地點、人物、事件）
          },
          {
            id: 'chinese.comprehension.explain',
            label: '說明文理解與主旨提取',
            status: 'ready',
            grade: 3.2,
            type: 'handwritten',
            moeCode: 'Bc-II-1',
            // Bc-II-1：說明文本的邏輯結構（列舉、因果、比較）
          },
        ],
      },
      {
        number: 5,
        title: '寫作基礎',
        moeRef: 'Be-II-1,Be-II-2',
        kps: [
          {
            id: 'chinese.writing.guided',
            label: '引導寫作（日記/書信/記事 填空式）',
            status: 'ready',
            moeCode: 'Be-II-2',
            // Be-II-2：應用文本（書信、日記、報告等）格式；Be-II-1：寫作策略
          },
          {
            id: 'chinese.writing.diary',
            label: '日記與書信等應用文寫作',
            status: 'ready',
            grade: 3.2,
            type: 'handwritten',
            moeCode: 'Be-II-1',
          },
        ],
      },
      {
        number: 6,
        title: '標點符號與修辭',
        moeRef: 'Ac-II-1,Ac-II-2',
        kps: [
          {
            id: 'chinese.punctuation.basic',
            label: '常用標點符號的正確用法',
            status: 'ready',
            grade: 3.2,
            type: 'handwritten',
            moeCode: 'Ac-II-1',
            // Ac-II-1：標點符號；複句句型
          },
          {
            id: 'chinese.rhetoric.basic',
            label: '比喻、擬人等基本修辭',
            status: 'ready',
            grade: 3.2,
            type: 'handwritten',
            moeCode: 'Ac-II-2',
          },
        ],
      },
      {
        number: 7,
        title: '文化內涵與成語',
        moeRef: 'Ca-II-1,Cb-II-1,Cc-II-1',
        kps: [
          {
            id: 'chinese.idiom.basic',
            label: '成語故事與應用',
            status: 'ready',
            grade: 3.2,
            type: 'handwritten',
            moeCode: 'Ca-II-1',
            // Ca-II-1：物質文化（含工具、食衣住行）
          },
          {
            id: 'chinese.culture.community',
            label: '社群文化與人際關係',
            status: 'ready',
            grade: 3.2,
            type: 'handwritten',
            moeCode: 'Cb-II-1',
            // Cb-II-1：社群文化（家庭關係、社會連結）
          },
        ],
      },
      // ── 三上 backport 章節 (grade: 3.1) — Y1.Q3 v1.1 第二批 ────────────
      // 康軒三上國語 4 主題 + 語文加油站；標 grade:3.1 與既有三下 KP 區分
      // 108課綱：Aa-II（語音）、Ab-II（識字）、Ac-II（句型）、Ad-II（篇章）、Be-II（寫作）
      {
        number: 8,
        title: '【三上】注音複習與聲調穩固',
        moeRef: 'Aa-II-1',
        kps: [
          {
            id: 'chinese.phonetic.tone.up',
            label: '三上注音複習（聲調、拼音穩固）',
            status: 'ready',
            moeCode: 'Aa-II-1',
            grade: 3.1,
            // Aa-II-1：標注注音符號，多音字聲調辨別；三上加強易混聲調
          },
        ],
      },
      {
        number: 9,
        title: '【三上】日常語詞（生活點滴 / 故事時間）',
        moeRef: 'Ab-II-3',
        kps: [
          {
            id: 'chinese.vocab.daily.up',
            label: '三上日常語詞（校園 / 家庭 / 秋天主題）',
            status: 'ready',
            moeCode: 'Ab-II-3',
            grade: 3.1,
            // Ab-II-3：常用詞彙的選用與搭配；三上主題偏生活記敘與節氣
          },
        ],
      },
      {
        number: 10,
        title: '【三上】簡單複句句型',
        moeRef: 'Ac-II-1',
        kps: [
          {
            id: 'chinese.sentence.simple.up',
            label: '三上複句句型（因果、轉折、遞進、假設）',
            status: 'ready',
            moeCode: 'Ac-II-1',
            grade: 3.1,
            // Ac-II-1：複句句型（因果、轉折）；三上為初學，三下進階修辭
          },
        ],
      },
      {
        number: 11,
        title: '【三上】短文段落理解（50-150字）',
        moeRef: 'Ad-II-2',
        kps: [
          {
            id: 'chinese.comprehension.short.up',
            label: '三上短文理解（校園家庭秋天節氣題材）',
            status: 'ready',
            moeCode: 'Ad-II-2',
            grade: 3.1,
            // Ad-II-2：段落大意歸納；三上短文 50-150 字，難度低於三下
          },
        ],
      },
      {
        number: 12,
        title: '【三上】引導寫作初級（填空式）',
        moeRef: 'Be-II-1',
        kps: [
          {
            id: 'chinese.writing.fillin.up',
            label: '三上填空式引導寫作（日記/人物/節日）',
            status: 'ready',
            moeCode: 'Be-II-1',
            grade: 3.1,
            // Be-II-1：寫作策略（描寫、敘事基礎）；三上為填空引導，三下進階獨立寫作
          },
        ],
      },
      // L01「許願」移到 chapters array 開頭(本檔案上方),boss 找「第一課」直覺在首位。

      // ── stage6 wire: meow911 段考真題綜合題庫(#250 coverage-fix)──────
      // 135 題段考真題:8 個小 bank (< 15Q) 已合併進 chinese.meow911.misc。
      // 保留 ≥ 15Q 的章節 KP:識字部首(22)/句型段落(18);其餘 95 題在 misc-pool。
      // L01-L07 既有 by-lesson chapter 不破壞,本 chapter 僅含 meow911 段考真題池。
      {
        number: 50,
        title: '【三下】📚 國語段考真題庫(主題 + 綜合)',
        moeRef: 'pending',
        // stage6 wire / meow911:135 題段考真題,#250 合併 8 個小 bank 後 wired
        kps: [
          { id: 'chinese.character.radical.meow911',    label: '🔤 識字部首 段考真題(22 題)',
            status: 'ready', grade: 3.2, type: 'handwritten', moeCode: 'Ab-II-2' },
          { id: 'chinese.sentence.pattern.meow911',     label: '📝 句型段落 段考真題(18 題)',
            status: 'ready', grade: 3.2, type: 'handwritten', moeCode: 'Ac-II-1' },
          { id: 'chinese.meow911.misc',                 label: '📚 國語綜合段考真題(95 題,含 L01-L07 + 閱讀寫作標點)',
            status: 'ready', grade: 3.2, type: 'handwritten', moeCode: 'pending' },
        ],
      },
    ],
  },

  // ────────────────────────────────────────────────────────────────
  // 🤖 英語 (翰林) — 對齊 108 課綱 第二學習階段
  // 參考來源：官方 k12ea.gov.tw PDF（語文領域-英語文）
  // 注意：108 課綱英語從三年級開始（無一二年級），
  //       整個第二學習階段（三四年級）共用同一組代碼，
  //       無獨立「三下」細分；以 "pending" 標記待細化代碼。
  // ────────────────────────────────────────────────────────────────
  {
    subject: 'english',
    label: '英語',
    emoji: '🤖',
    textbook: '翰林',
    chapters: [
      {
        number: 1,
        title: '字母拼讀（自然發音 Phonics）',
        moeRef: 'Ab-II',
        kps: [
          {
            id: 'english.alphabet.phonics',
            label: '自然發音基礎（字母與發音對應）',
            status: 'ready',
            moeCode: 'Ab-II',
            // Ab-II：語音（字母拼讀規則）；108 課綱英語重點項目
          },
          {
            id: 'english.phonics.basic',
            label: '拼讀規則進階（押韻、音節、母音）',
            status: 'ready',
            moeCode: 'Ab-II',
            // Ab-II：語音拼讀；母音/子音辨識、押韻、音節數
          },
        ],
      },
      {
        number: 2,
        title: '日常生活詞彙（家庭、學校、身體）',
        moeRef: 'Ac-II',
        kps: [
          {
            id: 'english.vocab.family',
            label: '家人稱謂單字',
            status: 'ready',
            moeCode: 'Ac-II',
            // Ac-II：字詞（生活常用詞彙）
          },
          {
            id: 'english.vocab.school',
            label: '學校與教室相關詞彙',
            status: 'ready',
            moeCode: 'Ac-II',
          },
        ],
      },
      {
        number: 3,
        title: '食物、動物與顏色詞彙',
        moeRef: 'Ac-II',
        kps: [
          {
            id: 'english.vocab.food',
            label: '食物飲料單字',
            status: 'ready',
            grade: 3.2,
            type: 'handwritten',
            moeCode: 'Ac-II',
          },
          {
            id: 'english.vocab.animals',
            label: '動物單字',
            status: 'ready',
            grade: 3.2,
            type: 'handwritten',
            moeCode: 'Ac-II',
          },
          {
            id: 'english.vocab.colors',
            label: '顏色單字',
            status: 'ready',
            moeCode: 'Ac-II',
          },
        ],
      },
      {
        number: 4,
        title: '數字、時間與日期',
        moeRef: 'Ac-II',
        kps: [
          {
            id: 'english.numbers.basic',
            label: '數字 1-100 聽讀練習',
            status: 'ready',
            grade: 3.2,
            type: 'handwritten',
            moeCode: 'Ac-II',
          },
          {
            id: 'english.numbers.ordinal',
            label: '序數（first, second…）',
            status: 'ready',
            grade: 3.2,
            type: 'handwritten',
            moeCode: 'Ac-II',
          },
        ],
      },
      {
        number: 5,
        title: '基本句型（be 動詞與現在簡單式）',
        moeRef: 'Ad-II',
        kps: [
          {
            id: 'english.grammar.questions',
            label: 'Yes/No 問句句型',
            status: 'ready',
            grade: 3.2,
            type: 'handwritten',
            moeCode: 'Ad-II',
            // Ad-II：句構（基本句型）
          },
          {
            id: 'english.grammar.statements',
            label: '肯定句與否定句',
            status: 'ready',
            grade: 3.2,
            type: 'handwritten',
            moeCode: 'Ad-II',
          },
        ],
      },
      {
        number: 6,
        title: '日常生活對話與情境表達',
        moeRef: 'Ae-II',
        kps: [
          {
            id: 'english.dialogue.daily',
            label: '問候、介紹、請求等情境對話',
            status: 'ready',
            moeCode: 'Ae-II',
            // Ae-II：篇章（簡短對話與情境語句）
          },
          {
            id: 'english.dialogue.classroom',
            label: '教室用語與指令理解',
            status: 'ready',
            grade: 3.2,
            type: 'handwritten',
            moeCode: 'Ae-II',
          },
        ],
      },
      // ── 三上 backport (grade: 3.1) — Y1.Q3 v1.1 第三批 ──────────────────
      // 翰林 Here We Go 3 上冊；標 grade:3.1 與既有三下 KP 區分
      // 108課綱：Aa-II（字母）、Ab-II（語音/拼讀）、Ac-II（字詞）、Ae-II（篇章對話）
      {
        number: 7,
        title: '【三上】字母大小寫複習（Here We Go 3 Warm-up）',
        moeRef: 'Aa-II',
        kps: [
          {
            id: 'english.alphabet.review.up',
            label: '三上字母大小寫辨識與順序（15Q）',
            status: 'ready',
            moeCode: 'Aa-II',
            grade: 3.1,
            // Aa-II：字母（大小寫辨識、字母順序）；三年級英語起點
          },
        ],
      },
      {
        number: 8,
        title: '【三上】學校與教室單字（Unit 3）',
        moeRef: 'Ac-II',
        kps: [
          {
            id: 'english.vocab.school.up',
            label: '三上校園 / 文具 / 教室單字（20Q）',
            status: 'ready',
            moeCode: 'Ac-II',
            grade: 3.1,
            // Ac-II：字詞（生活常用詞彙 — 學校場景）
          },
        ],
      },
      {
        number: 9,
        title: '【三上】顏色與形狀（Unit 4）',
        moeRef: 'Ac-II',
        kps: [
          {
            id: 'english.vocab.colors.up',
            label: '三上顏色 / 形狀單字（15Q）',
            status: 'ready',
            moeCode: 'Ac-II',
            grade: 3.1,
            // Ac-II：字詞（顏色、形狀常用詞彙）
          },
        ],
      },
      {
        number: 10,
        title: '【三上】問候與自我介紹（Unit 1）',
        moeRef: 'Ae-II',
        kps: [
          {
            id: 'english.greetings.intro.up',
            label: '三上問候語與自我介紹句型（15Q）',
            status: 'ready',
            moeCode: 'Ae-II',
            grade: 3.1,
            // Ae-II：篇章（簡短問候對話、自我介紹情境）
          },
        ],
      },

      // ── stage6 wire: meow911 段考真題綜合題庫(#250 coverage-fix)──────
      // 英語 49 題段考真題:4 個小 bank (< 15Q) 已合併進 english.meow911.misc。
      // 保留 ≥ 15Q 的 ch5 句型(18);其餘 31 題在 misc-pool。
      {
        number: 50,
        title: '【三下】📚 英語段考真題庫(對齊章節 + 綜合)',
        moeRef: 'pending',
        // stage6 wire / meow911:49 題段考真題,#250 合併 4 個小 bank 後 wired
        kps: [
          { id: 'english.grammar.questions.meow911', label: '🔤 ch5 句型 段考真題(18 題)',
            status: 'ready', grade: 3.2, type: 'handwritten', moeCode: 'Ad-II' },
          { id: 'english.meow911.misc', label: '📚 英語綜合段考真題(31 題,含 ch1-ch4 + 跨章節)',
            status: 'ready', grade: 3.2, type: 'handwritten', moeCode: 'pending' },
        ],
      },
    ],
  },

  // ────────────────────────────────────────────────────────────────
  // 🌀 自然 (南一) — 對齊 108 課綱 第二學習階段
  // 參考來源：guidelines.ms.cshs.tc.edu.tw/108/nature
  // 注意：自然科學課綱以兩年為一階段（三四年級共用），
  //       南一版三下的實際選題由教科書決定，代碼為對應階段代碼。
  // ────────────────────────────────────────────────────────────────
  {
    subject: 'nature',
    label: '自然',
    emoji: '🌀',
    textbook: '南一',
    chapters: [
      {
        number: 1,
        title: '植物的外部構造與功能',
        moeRef: 'INb-II-6,INb-II-7',
        kps: [
          {
            id: 'nature.plant.lifecycle',
            label: '植物根莖葉花果實種子的功能',
            status: 'ready',
            moeCode: 'INb-II-6',
            // INb-II-6：植物外部形態主要由根、莖、葉、花、果實及種子組成
          },
          {
            id: 'nature.plant.growth',
            label: '植物生長所需條件',
            status: 'ready',
            grade: 3.2,
            type: 'handwritten',
            moeCode: 'INa-II-7',
            // INa-II-7：生物需要能量（養分）、陽光、空氣、水和土壤
          },
        ],
      },
      {
        number: 2,
        title: '動物的特徵與分類',
        moeRef: 'INb-II-4,INb-II-5',
        kps: [
          {
            id: 'nature.animal.classify',
            label: '動物外部形態與分類特徵',
            status: 'ready',
            moeCode: 'INb-II-5',
            // INb-II-5：常見動物外部形態（頭、軀幹、肢），各類特徵差異
          },
          {
            id: 'nature.animal.adapt',
            label: '動植物構造與環境適應',
            status: 'ready',
            grade: 3.2,
            type: 'handwritten',
            moeCode: 'INb-II-7',
            // INb-II-7：外部形態和內部構造與生長、行為、繁衍和適應環境有關
          },
        ],
      },
      {
        number: 3,
        title: '水的三態變化',
        moeRef: 'INc-II-6,INa-II-4',
        kps: [
          {
            id: 'nature.water.states',
            label: '固態、液態、氣態與三態變化',
            status: 'ready',
            moeCode: 'INc-II-6',
            // INc-II-6：水有三態變化及毛細現象
          },
          {
            id: 'nature.water.temp',
            label: '溫度影響物質形態變化',
            status: 'ready',
            grade: 3.2,
            type: 'handwritten',
            moeCode: 'INa-II-4',
            // INa-II-4：物質的形態會因溫度的不同而改變
          },
        ],
      },
      {
        number: 4,
        title: '天氣與季節',
        moeRef: 'INd-II-6,INd-II-7,INf-II-4',
        kps: [
          {
            id: 'nature.weather.seasons',
            label: '四季氣溫變化與天氣預報',
            status: 'ready',
            moeCode: 'INd-II-6',
            // INd-II-6：一年四季氣溫會有所變化，天氣也會有所不同
          },
          {
            id: 'nature.weather.measure',
            label: '雨量、溫度、風向風速測量',
            status: 'ready',
            grade: 3.2,
            type: 'handwritten',
            moeCode: 'INd-II-7',
            // INd-II-7：天氣預報常用雨量、溫度、風向、風速資料
          },
        ],
      },
      {
        number: 5,
        title: '磁鐵的性質',
        moeRef: 'INe-II-7',
        kps: [
          {
            id: 'nature.magnet.basic',
            label: '磁鐵兩極的吸引與排斥',
            status: 'ready',
            grade: 3.2,
            type: 'handwritten',
            moeCode: 'INe-II-7',
            // INe-II-7：磁鐵有兩極，同極相斥異極相吸，磁力強弱比較
          },
        ],
      },
      {
        number: 6,
        title: '力的作用',
        moeRef: 'INc-II-3,INd-II-8,INd-II-9',
        kps: [
          {
            id: 'nature.machine.basic',
            label: '力的大小、方向與作用點',
            status: 'ready',
            moeCode: 'INc-II-3',
            // INc-II-3：力的表示法，包括大小、方向與作用點
          },
          {
            id: 'nature.force.deform',
            label: '施力使物體改變運動或形狀',
            status: 'ready',
            grade: 3.2,
            type: 'handwritten',
            moeCode: 'INd-II-9',
            // INd-II-9：施力可能使物體改變運動情形或形狀
          },
        ],
      },
      {
        number: 7,
        title: '聲音與光',
        moeRef: 'INe-II-5,INe-II-6',
        kps: [
          {
            id: 'nature.sound.light.basic',
            label: '物體振動產生聲音與傳播',
            status: 'ready',
            moeCode: 'INe-II-5',
            // INe-II-5：物體振動產生聲音，可透過固液氣傳播
          },
          {
            id: 'nature.light.basic',
            label: '光的直進與反射',
            status: 'ready',
            grade: 3.2,
            type: 'handwritten',
            moeCode: 'INe-II-6',
            // INe-II-6：光線以直線前進，反射時有一定方向
          },
        ],
      },
      {
        number: 8,
        title: '岩石與礦物',
        moeRef: 'INa-II-6',
        kps: [
          {
            id: 'nature.rock.basic',
            label: '岩石的特徵與礦物組成',
            status: 'ready',
            moeCode: 'INa-II-6',
            // INa-II-6：岩石由礦物組成，礦物具有獨特的物理性質可用於分類
          },
        ],
      },
      // ── 三上 backport 章節 (grade: 3.1) — Y1.Q3 v1.1 第四批，南一版 ───────
      // 對齊 108 課綱 INa-II（物質與能量）、INe-II（自然界的現象）
      // 標 grade:3.1 與既有三下 KP 區分
      {
        number: 9,
        title: '【三上】觀察與描述（科學方法入門）',
        moeRef: 'INa-II-1',
        kps: [
          {
            id: 'nature.observation.basic.up',
            label: '三上觀察記錄與感官應用（15Q）',
            status: 'ready',
            moeCode: 'INa-II-1',
            grade: 3.1,
            // INa-II-1：自然界的探究需要有系統地觀察、測量與記錄
          },
        ],
      },
      {
        number: 10,
        title: '【三上】水與空氣的特性',
        moeRef: 'INa-II-4',
        kps: [
          {
            id: 'nature.water.air.up',
            label: '三上水的三態與空氣性質（20Q）',
            status: 'ready',
            moeCode: 'INa-II-4',
            grade: 3.1,
            // INa-II-4：物質的形態會因溫度不同而改變（固液氣三態）
          },
        ],
      },
      {
        number: 11,
        title: '【三上】廚房裡的科學',
        moeRef: 'INa-II-2',
        kps: [
          {
            id: 'nature.kitchen.science.up',
            label: '三上溶解、氧化與生活化學（15Q）',
            status: 'ready',
            moeCode: 'INa-II-2',
            grade: 3.1,
            // INa-II-2：物質的外觀、形狀、大小等物理性質可用感官觀察或測量
          },
        ],
      },
      {
        number: 12,
        title: '【三上】光與影子',
        moeRef: 'INe-II-6',
        kps: [
          {
            id: 'nature.light.shadow.up',
            label: '三上光的直線傳播與影子（15Q）',
            status: 'ready',
            moeCode: 'INe-II-6',
            grade: 3.1,
            // INe-II-6：光線以直線前進，反射時有一定方向
          },
        ],
      },

      // ── stage6 wire: meow911 段考真題綜合題庫(#250 coverage-fix)──────
      // 自然 534 題段考真題:6 個小 bank (< 15Q) 已合併進 nature.meow911.misc。
      // 保留 ≥ 15Q 的 ch1 植物(324)/ch3 水三態(152);其餘 58 題在 misc-pool。
      {
        number: 50,
        title: '【三下】📚 自然段考真題庫(對齊章節 + 綜合)',
        moeRef: 'pending',
        // stage6 wire / meow911:534 題段考真題,#250 合併 6 個小 bank 後 wired
        kps: [
          { id: 'nature.plant.lifecycle.meow911', label: '🌱 ch1 植物 段考真題(324 題)',
            status: 'ready', grade: 3.2, type: 'handwritten', moeCode: 'INb-II-6' },
          { id: 'nature.water.states.meow911', label: '💧 ch3 水三態 段考真題(152 題)',
            status: 'ready', grade: 3.2, type: 'handwritten', moeCode: 'INc-II-6' },
          { id: 'nature.meow911.misc', label: '📚 自然綜合段考真題(58 題,含 ch2/ch4-ch8 + 跨單元)',
            status: 'ready', grade: 3.2, type: 'handwritten', moeCode: 'pending' },
        ],
      },
    ],
  },

  // ────────────────────────────────────────────────────────────────
  // 🏰 社會 (翰林) — 對齊 108 課綱 第二學習階段
  // 參考來源：官方 k12ea.gov.tw PDF（社會領域）—PDF 無法解析
  //           四大主題軸：互動與關聯、差異與多元、變遷與因果、選擇與責任
  //           翰林三年級單元：家庭、社區與地方（上學期）、地方特色與地圖（下學期）
  // 注意：社會領域官方 PDF 為加密二進位，無法解析出精確代碼；
  //       以 "pending" 標記，僅對齊主題軸描述。
  // ────────────────────────────────────────────────────────────────
  {
    subject: 'social',
    label: '社會',
    emoji: '🏰',
    textbook: '翰林',
    chapters: [
      {
        number: 1,
        title: '家庭與社區的互動',
        moeRef: 'pending',
        // 主題軸：互動與關聯。家庭成員、角色分工與社區關係。
        kps: [
          {
            id: 'social.community.basic',
            label: '家庭與社區的關係',
            status: 'ready',
            moeCode: 'pending',
          },
          {
            id: 'social.family.roles',
            label: '家庭成員角色與責任',
            status: 'ready',    // bank: social-family-roles-翰林.json
            grade: 3.2,
            type: 'handwritten',
            moeCode: 'pending',
          },
        ],
      },
      {
        number: 2,
        title: '地方的自然環境',
        moeRef: 'pending',
        // 主題軸：互動與關聯 / 差異與多元。地形、氣候、自然景觀。
        kps: [
          {
            id: 'social.taiwan.geography',
            label: '台灣地形與地理位置',
            status: 'ready',
            moeCode: 'pending',
          },
          {
            id: 'social.local.environment',
            label: '居住地方的自然環境特色',
            status: 'ready',    // bank: social-local-environment-翰林.json
            grade: 3.2,
            type: 'handwritten',
            moeCode: 'pending',
          },
        ],
      },
      {
        number: 3,
        title: '我的家鄉與在地特色',
        moeRef: 'pending',
        // 主題軸：差異與多元 / 我與環境。家鄉認識、地方特產與族群文化。
        kps: [
          {
            id: 'social.hometown.basic',
            label: '家鄉的特色與在地認同',
            status: 'ready',
            moeCode: 'pending',
          },
          {
            id: 'social.custom.festival',
            label: '傳統節慶與習俗（含原住民族、客家）',
            status: 'ready',
            moeCode: 'pending',
          },
        ],
      },
      {
        number: 4,
        title: '生活規範與公民責任',
        moeRef: 'pending',
        // 主題軸：選擇與責任 / 我與群體。法律、公德心、社會秩序。
        kps: [
          {
            id: 'social.life.rule',
            label: '法律、公德心與生活規範',
            status: 'ready',
            moeCode: 'pending',
          },
        ],
      },
      {
        number: 5,
        title: '交通、公共設施與安全',
        moeRef: 'pending',
        // 主題軸：選擇與責任 / 互動與關聯。交通工具、大眾運輸、交通規則。
        kps: [
          {
            id: 'social.traffic.basic',
            label: '台灣交通工具與大眾運輸',
            status: 'ready',
            moeCode: 'pending',
          },
        ],
      },
      {
        number: 6,
        title: '自然環境與人類使用',
        moeRef: 'pending',
        // 主題軸：我與環境 / 選擇與責任。土地利用、環保、永續。
        kps: [
          {
            id: 'social.environment.basic',
            label: '自然環境的人類使用與環保行動',
            status: 'ready',
            moeCode: 'pending',
          },
        ],
      },
      // ── 三上 backport 章節 (grade: 3.1) — Y1.Q3-SOC v1.1 最後一科 ─────────
      // 翰林三上社會；對齊 108 課綱四大主題軸（我與自我、我與群體、我與環境、我與時空）
      // 標 grade:3.1 與既有三下 KP 區分；moeCode 標 pending（社會領域 PDF 無法解析）
      {
        number: 7,
        title: '【三上】我與自己：自我介紹與家庭成員',
        moeRef: 'pending',
        // 主題軸：我與自我 / 我與群體。自我認識、家庭成員稱謂。翰林三上第一單元。
        kps: [
          {
            id: 'social.self.family.up',
            label: '三上自我介紹與家庭成員稱謂（15Q）',
            status: 'ready',
            moeCode: 'pending',
            grade: 3.1,
          },
        ],
      },
      {
        number: 8,
        title: '【三上】我與群體：校園生活與場所',
        moeRef: 'pending',
        // 主題軸：我與群體 / 互動與關聯。學校各空間功能、校園規則。翰林三上第二單元。
        kps: [
          {
            id: 'social.school.life.up',
            label: '三上校園場所與生活規則（15Q）',
            status: 'ready',
            moeCode: 'pending',
            grade: 3.1,
          },
        ],
      },
      {
        number: 9,
        title: '【三上】我與時空：時間概念（早午晚 / 昨今明）',
        moeRef: 'pending',
        // 主題軸：我與時空 / 變遷與因果。早/午/晚時段、昨今明時序概念。翰林三上第三單元。
        kps: [
          {
            id: 'social.time.place.up',
            label: '三上時間時段與時序概念（15Q）',
            status: 'ready',
            moeCode: 'pending',
            grade: 3.1,
          },
        ],
      },
      {
        number: 10,
        title: '【三上】我與群體：規則與規範初步',
        moeRef: 'pending',
        // 主題軸：選擇與責任 / 我與群體。公共場所規範、基本生活規則。翰林三上第四單元。
        kps: [
          {
            id: 'social.rules.basic.up',
            label: '三上公共規則與基本規範（15Q）',
            status: 'ready',
            moeCode: 'pending',
            grade: 3.1,
          },
        ],
      },

      // ── stage6 wire: meow911 段考真題綜合題庫(#250 coverage-fix)──────
      // 社會 386 題段考真題:1 個小 bank (ch4 生活規範 1Q) 已合併進 social.meow911.misc。
      // 保留 ≥ 15Q 的 ch1/ch2/ch3/ch5/ch6;其餘 88 題在 misc-pool。
      {
        number: 50,
        title: '【三下】📚 社會段考真題庫(對齊章節 + 綜合)',
        moeRef: 'pending',
        // stage6 wire / meow911:386 題段考真題,#250 合併 1 個小 bank 後 wired
        kps: [
          { id: 'social.community.basic.meow911', label: '🏠 ch1 家庭社區 段考真題(184 題)',
            status: 'ready', grade: 3.2, type: 'handwritten', moeCode: 'pending' },
          { id: 'social.taiwan.geography.meow911', label: '🗺 ch2 地理環境 段考真題(34 題)',
            status: 'ready', grade: 3.2, type: 'handwritten', moeCode: 'pending' },
          { id: 'social.hometown.basic.meow911', label: '🏞 ch3 家鄉特色 段考真題(38 題)',
            status: 'ready', grade: 3.2, type: 'handwritten', moeCode: 'pending' },
          { id: 'social.traffic.basic.meow911', label: '🚌 ch5 交通公共 段考真題(17 題)',
            status: 'ready', grade: 3.2, type: 'handwritten', moeCode: 'pending' },
          { id: 'social.environment.basic.meow911', label: '🌳 ch6 環境環保 段考真題(25 題)',
            status: 'ready', grade: 3.2, type: 'handwritten', moeCode: 'pending' },
          { id: 'social.meow911.misc', label: '📚 社會綜合段考真題(88 題,含 ch4 + 跨單元)',
            status: 'ready', grade: 3.2, type: 'handwritten', moeCode: 'pending' },
        ],
      },
    ],
  },
];

export function getSubject(id: Subject): SubjectCurriculum | undefined {
  return CURRICULUM.find(s => s.subject === id);
}

export function getKPStatus(kpId: string): 'ready' | 'wip' | 'unknown' {
  for (const s of CURRICULUM) {
    for (const c of s.chapters) {
      for (const k of c.kps) {
        if (k.id === kpId) return k.status;
      }
    }
  }
  return 'unknown';
}

/**
 * Resolve a knowledge-point id to its human-friendly Chinese label.
 *
 * Used wherever the UI would otherwise print raw KP ids like `math.fraction.compare`
 * — particularly the parent dashboard's 弱點/強項 lists and academy parent-mode
 * meta. If the kpId is not found in CURRICULUM, the original id is returned so
 * the UI still shows something stable rather than empty text.
 *
 * Lookup is O(N×C×K) across the curriculum tree. CURRICULUM is small (<200 KPs)
 * so this is cheap; no need to memoise.
 */
export function getKPLabel(kpId: string): string {
  for (const s of CURRICULUM) {
    for (const c of s.chapters) {
      for (const k of c.kps) {
        if (k.id === kpId) return k.label;
      }
    }
  }
  return kpId;
}

/**
 * Resolve a knowledge-point id to its enclosing chapter (number + title).
 *
 * Used by academy.ts to display the kid's current chapter on the subject card
 * (replacing the placeholder 「今天的章節」 — 🧒 小明 2026-05-21 01:14 leftover fix).
 * Returns null if the kpId is not found in CURRICULUM so callers can fall back
 * to a sensible default ("點擊開始" / "等你來").
 */
export function getChapterForKP(kpId: string): { number: number; title: string } | null {
  for (const s of CURRICULUM) {
    for (const c of s.chapters) {
      for (const k of c.kps) {
        if (k.id === kpId) return { number: c.number, title: c.title };
      }
    }
  }
  return null;
}
